<template>
  <v-app>
    <employee-shift-mapping-setup
      :dialog="dialog"
      :data="formData"
      :shift-list="shiftList"
      :employee-list="employeeList"
      :selected-employee="selectedEmployee"
      @update:dialog="dialog = $event"
      @saved="handleSaved"
    />
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Employee & Shift Mapping
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col lg="3" md="4" cols="12">
                    <v-autocomplete
                      v-model="selectedEmployee"
                      :items="employeeList"
                      :loading="employeeLoading"
                      :item-title="employeeLabel"
                      item-value="id"
                      label="Select Employee"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details
                    >
                      <template #append-inner>
                        <v-icon color="primary">mdi-account</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col lg="6" md="4" cols="12">
                    <div class="d-flex justify-end align-center gap-2 pr-2 flex-wrap">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">
                          CSV
                        </v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">
                          EXCEL
                        </v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">
                          PDF
                        </v-btn>
                      </v-btn-group>
                      <v-btn
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                        class="ms-2"
                        @click="openAddMapping"
                      >
                        Assign Shift
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="formattedMappings"
                  :loading="loading"
                  :search="search"
                  :hover="true"
                  v-model:items-per-page="itemsPerPage"
                  density="compact"
                >
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>
                  <template #item.actions="{ item }">
                    <v-icon
                      v-if="$updateAccess($route.fullPath)"
                      class="me-2 edit-button"
                      color="primary"
                      @click="editMapping(item)"
                    >
                      mdi-pencil-outline
                    </v-icon>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { toast } from "vue3-toastify";
import employeeShiftMappingSetup from "@/components/module/attendance/settingsManagement/employeeShiftMappingSetup.vue";

export default {
  components: {
    employeeShiftMappingSetup,
  },
  data: () => ({
    panel: [0],
    dialog: false,
    formData: {},
    selectedEmployee: null,
    search: "",
    loading: false,
    employeeLoading: false,
    itemsPerPage: 10,
    headers: [
      { title: "#SL", key: "index", align: "center", sortable: false, width: "7%" },
      { title: "Employee", key: "employee", align: "left", sortable: false },
      { title: "Shift", key: "shift_label", align: "left", sortable: false },
      { title: "Start Date", key: "start_date", align: "center", sortable: false },
      { title: "End Date", key: "end_date", align: "center", sortable: false },
      { title: "Remarks", key: "remarks", align: "left", sortable: false },
      { title: "Actions", key: "actions", align: "center", sortable: false },
    ],
  }),
  computed: {
    employeeList() {
      return this.$store.getters.getHrmEmployeeList || [];
    },
    mappingList() {
      return this.$store.getters.getEmployeeShiftMappingList || [];
    },
    shiftList() {
      return this.$store.getters.getShiftList || [];
    },
    formattedMappings() {
      return this.mappingList.map((item) => {
        const shift = item?.shift ?? {};
        const shiftLabel = this.formatShiftLabel(shift);
        return {
          id: item.id,
          employee_id: item.hrm_employee_id,
          shift_id: item.att_shift_id,
          employee: item?.employee
            ? this.formatEmployeeLabel(item.employee)
            : "-",
          shift_label: shiftLabel,
          start_date: item?.start_date || "-",
          end_date: item?.end_date || "-",
          remarks: item?.remarks || "-",
          raw: item,
        };
      });
    },
  },
  watch: {
    selectedEmployee(newVal) {
      if (newVal) {
        this.fetchMappings({ employee_id: newVal });
      } else {
        this.fetchMappings();
      }
    },
  },
  created() {
    this.initializeData();
  },
  methods: {
    employeeLabel(item) {
      if (!item) return "";
      if (item.code) {
        return `${item.code} - ${item.name}`;
      }
      return item.name;
    },
    async initializeData() {
      this.employeeLoading = true;
      try {
        if (this.$store.getters.getHrmEmployeeList.length === 0) {
          await this.$store.dispatch("getHrmEmployeeList");
        }
        if (this.$store.getters.getShiftList.length === 0) {
          await this.$store.dispatch("getShiftList");
        }
      } catch (error) {
        const message = error?.response?.data?.message || "Unable to load initial data.";
        toast.error(message);
      } finally {
        this.employeeLoading = false;
        this.fetchMappings(this.selectedEmployee ? { employee_id: this.selectedEmployee } : undefined);
      }
    },
    async fetchMappings(params = undefined) {
      this.loading = true;
      if (params && params.employee_id) {
        this.$store
          .dispatch("getEmployeeShiftMappingList", params)
          .catch((error) => {
            const message = error?.response?.data?.message || "Unable to load mappings.";
            toast.error(message);
          })
          .finally(() => {
            this.loading = false;
          });
        return;
      }

      const employees = this.employeeList;
      if (!employees.length) {
        this.$store.commit("UNSET_EMPLOYEE_SHIFT_MAPPING_LIST");
        this.loading = false;
        return;
      }

      const combined = [];
      const results = await Promise.allSettled(
        employees.map((employee) =>
          this.$store.dispatch("getEmployeeShiftMappingList", {
            employee_id: employee.id,
            __suppressCommit: true,
          })
        )
      );

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const data = result.value?.data?.data ?? [];
          data.forEach((item) => {
            const enriched = {
              ...item,
            };
            if (!enriched.employee && employees[index]) {
              enriched.employee = {
                id: employees[index].id,
                name: employees[index].name,
                code: employees[index].code ?? "",
              };
            }
            combined.push(enriched);
          });
        } else {
          const employee = employees[index];
          const message =
            result.reason?.response?.data?.message ||
            `Unable to load shifts for ${employee?.name ?? "employee"}.`;
          toast.error(message);
        }
      });

      this.$store.commit("SET_EMPLOYEE_SHIFT_MAPPING_LIST", combined);
      this.loading = false;
    },
    openAddMapping() {
      this.formData = {
        employee_shift_id: "",
        employee_id: this.selectedEmployee || "",
        shift_id: "",
        start_date: "",
        end_date: "",
        remarks: "",
      };
      this.dialog = true;
    },
    editMapping(item) {
      this.formData = {
        employee_shift_id: item.id,
        employee_id: item.employee_id || this.selectedEmployee || "",
        shift_id: item.shift_id,
        start_date: item.start_date,
        end_date: item.end_date,
        remarks: item.remarks,
      };
      this.dialog = true;
    },
    handleSaved() {
      this.dialog = false;
      this.fetchMappings(this.selectedEmployee ? { employee_id: this.selectedEmployee } : undefined);
    },
    formatEmployeeLabel(employee) {
      if (!employee) return "-";
      const code = employee.code ? `${employee.code} ` : "";
      return `${code}${employee.name ?? ""}`.trim() || "-";
    },
    formatShiftLabel(shift) {
      if (!shift) return "-";
      const name = shift.shift_name || "Unnamed Shift";
      const inTime = this.formatTime(shift.in_time);
      const outTime = this.formatTime(shift.out_time);
      if (!inTime && !outTime) {
        return name;
      }
      return `${name} (${inTime || "--:--"} - ${outTime || "--:--"})`;
    },
    formatTime(value) {
      if (!value) return "";
      const timeString = String(value);
      if (timeString.length >= 5) {
        return timeString.slice(0, 5);
      }
      return timeString;
    },
    exportToPDF() {
      let serial = 1;
      const columns = ["#SL", "Employee", "Shift", "Start Date", "End Date", "Remarks"];
      const rows = this.formattedMappings.map((item) => [
        serial++,
        item.employee,
        item.shift_label,
        item.start_date,
        item.end_date,
        item.remarks,
      ]);
      this.$exportTableToPDF(columns, rows, "Employee Shift Mapping", `employee_shift_mapping_${this.$fileDate()}`);
    },
    exportToExcel() {
      const data = [["#SL", "Employee", "Shift", "Start Date", "End Date", "Remarks"]];
      let serial = 1;
      this.formattedMappings.forEach((item) => {
        data.push([
          serial++,
          item.employee,
          item.shift_label,
          item.start_date,
          item.end_date,
          item.remarks,
        ]);
      });
      this.$exportTableToExcel(data, `employee_shift_mapping_${this.$fileDate()}`);
    },
    exportToCSV() {
      const data = [["#SL", "Employee", "Shift", "Start Date", "End Date", "Remarks"]];
      let serial = 1;
      this.formattedMappings.forEach((item) => {
        data.push([
          serial++,
          item.employee,
          item.shift_label,
          item.start_date,
          item.end_date,
          item.remarks,
        ]);
      });
      this.$exportTableToCSV(data, `employee_shift_mapping_${this.$fileDate()}`);
    },
  },
};
</script>

